import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertTriangle,
  ArrowLeftRight,
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  LayoutDashboard,
  LineChart,
  Package,
  Route,
  Shield,
  ShoppingCart,
  SlidersHorizontal,
  Truck,
} from 'lucide-react';
import { KpiCard } from '@/components/shared/KpiCard';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { homeService, type HomeAlertStats } from '@/modules/home/services/home.service';
import { prefetchSupplyTable } from '@/utils/supplyPrefetch';

interface ModuleTile {
  title: string;
  path: string;
  icon: React.ElementType;
  color: string;
  moduleKey: string;
  route: string;
  defaultVisible?: boolean;
}

const EMPTY_STATS: HomeAlertStats = {
  urgentProducts: 0,
  suppliersToReview: 0,
  pendingOrdersToReceive: 0,
};

export function HomePage() {
  const { t } = useTranslation();
  const warehouseId = useWarehouseId();
  const { isModuleVisible, isRouteEnabled } = useFeatureVisibility();
  const [stats, setStats] = useState<HomeAlertStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const supplyVisible = isModuleVisible('supply', true) && isRouteEnabled('/supply', true);

  useEffect(() => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      setStats(EMPTY_STATS);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    if (supplyVisible) {
      prefetchSupplyTable(warehouseId);
    }

    void homeService
      .getAlertStats(warehouseId)
      .then((data) => {
        if (!cancelled) setStats(data);
      })
      .catch((err) => {
        console.error('[HomePage] error loading stats:', err);
        if (!cancelled) {
          setError(t('home.error.loadFailed'));
          setStats(EMPTY_STATS);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [warehouseId, supplyVisible, t]);

  const modules = useMemo<ModuleTile[]>(
    () => {
      // Mismo orden que el sidebar, empezando por Forecast.
      const allModules: ModuleTile[] = [
        {
          title: t('sidebar.demandForecast'),
          path: '/forecast',
          icon: LineChart,
          color: 'bg-indigo-500',
          moduleKey: 'forecast',
          route: '/forecast',
        },
        {
          title: t('sidebar.sop'),
          path: '/demand-planning/sop/plan',
          icon: BarChart3,
          color: 'bg-purple-500',
          moduleKey: 'demand_planning',
          route: '/demand-planning',
        },
        {
          title: t('navigation.inventoryPolicies'),
          path: '/inventory-policies',
          icon: Package,
          color: 'bg-orange-500',
          moduleKey: 'inventory_policies',
          route: '/inventory-policies',
        },
        {
          title: t('navigation.inventorySimulation'),
          path: '/inventory-simulation',
          icon: Activity,
          color: 'bg-sky-600',
          moduleKey: 'inventory_simulation',
          route: '/inventory-simulation',
          defaultVisible: false,
        },
        {
          title: t('navigation.networkReplenishment'),
          path: '/network-replenishment',
          icon: ArrowLeftRight,
          color: 'bg-violet-600',
          moduleKey: 'network_replenishment',
          route: '/network-replenishment',
          defaultVisible: false,
        },
        {
          title: t('navigation.safetyStock'),
          path: '/safety-stock',
          icon: Shield,
          color: 'bg-rose-600',
          moduleKey: 'safety_stock',
          route: '/safety-stock',
          defaultVisible: false,
        },
        {
          title: t('navigation.inventoryPolicyControl'),
          path: '/inventory-policy-control',
          icon: LayoutDashboard,
          color: 'bg-slate-600',
          moduleKey: 'inventory_policy_control',
          route: '/inventory-policy-control',
          defaultVisible: false,
        },
        {
          title: t('navigation.businessRules'),
          path: '/business-rules',
          icon: SlidersHorizontal,
          color: 'bg-gray-600',
          moduleKey: 'business_rules',
          route: '/business-rules',
        },
        {
          title: t('navigation.supply'),
          path: '/supply',
          icon: ShoppingCart,
          color: 'bg-[#1681ba]',
          moduleKey: 'supply',
          route: '/supply',
        },
        {
          title: t('navigation.orderTracking'),
          path: '/order-tracking',
          icon: Route,
          color: 'bg-cyan-600',
          moduleKey: 'order_tracking',
          route: '/order-tracking',
        },
        {
          title: t('navigation.orders'),
          path: '/orders',
          icon: ClipboardList,
          color: 'bg-green-600',
          moduleKey: 'orders',
          route: '/orders',
        },
        {
          title: t('navigation.customerFulfillment'),
          path: '/customer-fulfillment',
          icon: ClipboardCheck,
          color: 'bg-amber-600',
          moduleKey: 'customer_fulfillment',
          route: '/customer-fulfillment',
        },
      ];
      return allModules.filter(
        (mod) =>
          isModuleVisible(mod.moduleKey, mod.defaultVisible ?? true)
          && isRouteEnabled(mod.route, mod.defaultVisible ?? true),
      );
    },
    [isModuleVisible, isRouteEnabled, t],
  );

  if (!warehouseId) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-white rounded-lg p-6 text-gray-600 border border-gray-200">
          {t('warehouse.noSelection')}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <h2 className="text-lg font-semibold mb-2">{t('home.error.title')}</h2>
        <p>{error}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200"
        >
          {t('home.error.retry')}
        </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {supplyVisible ? (
          <Link to="/supply" className="block h-full">
            <KpiCard
              title={t('home.alerts.urgentProducts')}
              value={stats.urgentProducts}
              isCount
              variant={stats.urgentProducts > 0 ? 'danger' : 'default'}
              icon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
              loading={loading}
            />
          </Link>
        ) : (
          <KpiCard
            title={t('home.alerts.urgentProducts')}
            value="—"
            icon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
          />
        )}

        {supplyVisible ? (
          <Link to="/supply" className="block h-full">
            <KpiCard
              title={t('home.alerts.suppliersToReview')}
              value={stats.suppliersToReview}
              isCount
              variant={stats.suppliersToReview > 0 ? 'warning' : 'default'}
              icon={<Truck className="h-5 w-5 text-blue-500" />}
              loading={loading}
            />
          </Link>
        ) : (
          <KpiCard
            title={t('home.alerts.suppliersToReview')}
            value="—"
            icon={<Truck className="h-5 w-5 text-blue-500" />}
          />
        )}

        <KpiCard
          title={t('home.alerts.pendingOrdersToReceive')}
          value={stats.pendingOrdersToReceive}
          isCount
          variant={stats.pendingOrdersToReceive > 0 ? 'warning' : 'default'}
          icon={<Package className="h-5 w-5 text-[#1681ba]" />}
          loading={loading}
        />
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('home.modulesTitle')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {modules.map((module) => (
            <Link
              key={module.path}
              to={module.path}
              className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow border border-gray-200"
            >
              <div className={`h-2 ${module.color}`} />
              <div className="p-5">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${module.color} bg-opacity-10`}>
                    <module.icon className={`h-6 w-6 ${module.color.replace('bg-', 'text-')}`} />
                  </div>
                  <h3 className="font-semibold text-gray-900">{module.title}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
